// Pathways 12 & 13 — Fresher Launchpad + Career Switch & Growth.
// Data: src/data/fresherCareerSwitch.json (211 shared resources, filtered by `fits`).
import React, { useState, useEffect, useMemo } from 'react';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, X, ExternalLink, ShieldCheck, Globe, ChevronDown, BadgeCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import data from '../data/fresherCareerSwitch.json';
import { GuideAccordions, GuideModal, Row, RouteLinks, StateGuide, ExtBtn, Value } from '../components/resource-pathways/GuideParts';

const SOURCES = Object.fromEntries((data.source_register || []).map((s) => [s.id, s]));
const PAGE = 12;
const KIND_LABEL = {
  apprenticeship_or_internship: 'Apprenticeship / Internship',
  government_or_public_careers: 'Government & public careers',
  official_learning: 'Official learning',
  company_careers: 'Company careers',
  return_to_work: 'Return to work',
};
const domainOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };
const text = (r) => `${r.name} ${r.provider_name} ${r.preview_fresher} ${r.preview_professional} ${(r.goals||[]).join(' ')} ${(r.categories||[]).join(' ')} ${r.eligibility} ${r.availability} ${r.audience||''} ${Object.values(r.full_preview||{}).join(' ')} ${r.learning_domain||''}`.toLowerCase();

const GEO_NOTICE = 'Built for Indian users. This collection includes India-specific and global official destinations. A global portal does not guarantee an India opening — always check the live location and eligibility.';

/* Resource details — full preview, practice, India check, verification and sources (v4.1.0). */
function ResourceModal({ r, cfg, onClose }) {
  const fp = r.full_preview || {};
  const ver = r.verification || {};
  const hist = r.historical_recorded_link_conditions;
  return (
    <GuideModal title={r.name} badge={KIND_LABEL[r.kind] || r.kind} onClose={onClose} tid={cfg.tid}
      footer={(
        <>
          <ExtBtn href={r.official_url} label={r.cta || 'Visit official destination'} tid={`${cfg.tid}-modal-link-${r.id}`} />
          <p className="text-[12px] text-gray-500 mt-2">{domainOf(r.official_url)} · opens in a new tab — you are leaving LearnixTa. {r.availability}</p>
        </>
      )}>
      <Row label="Preview" value={cfg.fit === 'fresher_launchpad' ? r.preview_fresher : r.preview_professional} />
      {Object.entries(fp).map(([k, v]) => <Row key={k} label={k.replace(/_/g, ' ')} value={v} />)}
      {r.practice && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3" data-testid={`${cfg.tid}-modal-practice`}>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-amber-800 mb-1">Practical exercise</p>
          <p className="text-[15px] text-gray-800">{r.practice.task}</p>
          {r.practice.success_check && <p className="text-sm text-gray-700 mt-1"><span className="font-bold">Success check:</span> {r.practice.success_check}</p>}
          {r.practice.status && <p className="text-xs text-gray-500 mt-1">{r.practice.status}</p>}
        </div>
      )}
      <Row label="Audience" value={r.audience} />
      <Row label="Eligibility" value={r.eligibility} />
      {r.india_availability && <Row label="India availability" value={`${r.india_availability.check || ''}`} />}
      {r.kind_correction_note && <Row label="Kind correction" value={r.kind_correction_note} />}
      <Row label="Safety note" value={r.safety_note} />
      <div className="text-[13px] text-gray-600 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 space-y-1" data-testid={`${cfg.tid}-modal-verification`}>
        <p><span className="font-bold text-gray-800">Content reviewed:</span> {r.content_reviewed_at || r.reviewed_at}{ver.previous_catalogue_review_date ? ` · previous catalogue review ${ver.previous_catalogue_review_date}` : ''}.</p>
        <p><span className="font-bold text-gray-800">Current opening verified:</span> {ver.current_opening_verified ? 'Yes' : 'No — destination reference only, not a live vacancy.'}</p>
        {ver.note && <p>{ver.note}</p>}
        {r.source_ids?.length > 0 && <p>Sources: {r.source_ids.map((i) => SOURCES[i]).filter(Boolean).map((s, i) => <span key={s.id}>{i > 0 && ' · '}<a href={s.url} target="_blank" rel="noopener noreferrer" className="underline text-teal-700">{s.title}</a></span>)}</p>}
        {hist?.text && <p className="text-gray-500"><span className="font-bold">Recorded link conditions ({hist.recorded_review_date}, {String(hist.status).replace(/_/g, ' ')}):</span> {hist.text}</p>}
        {r.legal_note && <p className="text-gray-500">{r.legal_note}</p>}
      </div>
    </GuideModal>
  );
}

/* Pathway-level guide modules (state/UT guidance, AI workforce, decision tools…). */
function PathwayGuides({ cfg }) {
  const g = data.guides?.[cfg.fit];
  if (!g) return null;
  const skip = new Set(['title', 'schema_version', 'last_reviewed', 'verification_standard', 'publication_rules', 'safety_panel', 'independence_disclaimer', 'india_coverage', 'alternative_routes', 'national_internship_reference', 'release_notes', 'review_method', 'first_experience_note']);
  const titled = (k) => ({
    learning_journey: 'Learning journey', india_guidance: g.india_guidance?.heading || 'India guidance', ai_workforce_reality: g.ai_workforce_reality?.heading || 'AI workforce reality — labelled scenarios',
    decision_tools: 'Decision tools — comparison columns', learning_domains: 'Learning domains — foundations, practice and automation', india_decision_guidance: 'India decision guidance — location, documents, costs',
    sector_exploration: 'Sector exploration — examples, not vacancies', national_qualification_routes: 'National qualification routes', fresher_starting_points: 'Where to start — by profile',
    india_action_plan: 'India action plan', career_switch_india_guide: 'Career switch — India guide',
  }[k] || k.replace(/_/g, ' '));
  const modules = Object.keys(g).filter((k) => !skip.has(k) && g[k] && typeof g[k] === 'object').map((k) => ({ id: k, title: titled(k), body: Value({ v: g[k], reg: SOURCES }) }));
  const links = [...(g.alternative_routes?.links || []), ...(g.national_internship_reference ? [g.national_internship_reference] : [])];
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 mt-12" data-testid={`${cfg.tid}-guide-section`}>
      <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-widest mb-1">{g.india_coverage?.heading || 'Find a practical route in your state or union territory'}</h2>
      <p className="text-[13px] text-gray-500 mb-4">Reviewed {g.last_reviewed || data.last_reviewed}. Guidance and directories, not a live vacancy feed. Predictions are labelled scenarios.</p>
      <div className="bg-white border-2 border-teal-100 rounded-3xl p-5 md:p-6 mb-6">
        <StateGuide coverage={g.india_coverage} reg={SOURCES} tid={cfg.tid} />
      </div>
      <GuideAccordions tid={cfg.tid} modules={[
        ...modules,
        (g.review_method || g.release_notes) && { id: 'review', title: 'Content review, limits and change notes', body: Value({ v: { verification_standard: g.verification_standard, ...(g.review_method || {}), release_notes: g.release_notes }, reg: SOURCES }) },
        { id: 'sources', title: `Sources (${data.source_register?.length || 0})`, body: <ol className="space-y-1.5 text-sm">{(data.source_register || []).map((s, i) => <li key={s.id}>{i + 1}. <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline text-teal-700 break-all">{s.title}</a>{s.publisher ? ` — ${s.publisher}` : ''}{s.access_status ? <span className="block text-xs text-gray-500">{s.access_status}{s.reviewed_on ? ` · reviewed ${s.reviewed_on}` : ''}</span> : null}</li>)}</ol> },
      ]} />
      {links.length > 0 && (
        <div className="mt-6" data-testid={`${cfg.tid}-alt-routes`}>
          <h3 className="font-bold text-gray-900 mb-1">{g.alternative_routes?.heading || 'Compare other routes'}</h3>
          {g.alternative_routes?.text && <p className="text-sm text-gray-600 mb-3">{g.alternative_routes.text}</p>}
          {g.first_experience_note && <p className="text-sm text-gray-600 mb-3">{g.first_experience_note}</p>}
          <RouteLinks links={links} tid={cfg.tid} />
        </div>
      )}
    </section>
  );
}

function ResourcePathway({ cfg }) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const kind = params.get('kind') || 'all';
  const goal = params.get('goal') || '';
  const [shown, setShown] = useState(PAGE);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const prev = document.title;
    document.title = `${cfg.title} | LearnixTa`;
    return () => { document.title = prev; };
  }, [cfg.title]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const pool = useMemo(() => data.resources.filter((r) => r.fits.includes(cfg.fit)), [cfg.fit]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const g = goal.toLowerCase();
    return pool
      .filter((r) => (kind === 'all' || r.kind === kind))
      .filter((r) => (!g || (r.goals || []).some((x) => x.toLowerCase().includes(g)) || text(r).includes(g)))
      .filter((r) => (!s || text(r).includes(s)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [pool, q, kind, goal]);

  useEffect(() => { setShown(PAGE); }, [q, kind, goal]);
  const visible = filtered.slice(0, shown);

  const pillCls = (active) => `min-h-[44px] px-4 py-2 rounded-full text-[13px] font-bold border-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-700 ${active ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-teal-100 text-gray-700 hover:border-teal-400'}`;

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />
      <main className="pt-28 md:pt-32 pb-16">
        <section className="max-w-7xl mx-auto px-4 md:px-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 mb-6 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600" data-testid={`${cfg.tid}-back-link`}>
            <ArrowLeft size={15} aria-hidden="true" /> Back to Pathways
          </Link>
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-teal-700 mb-4">{cfg.eyebrow}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-[1.05]" data-testid={`${cfg.tid}-heading`}>{cfg.heading}</h1>
            <p className="text-base md:text-lg text-gray-600 mt-5 leading-relaxed">{cfg.copy}</p>
            <p className="mt-5 text-[13px] text-gray-500 flex items-center gap-2 flex-wrap" data-testid={`${cfg.tid}-trust-line`}>
              <ShieldCheck size={14} className="text-teal-600 shrink-0" aria-hidden="true" /> {cfg.trust}
            </p>
          </div>
        </section>

        {/* Goal selector (switch page) / starting cards (fresher page) */}
        {cfg.goals && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10" aria-label="Pick a goal">
            <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-widest mb-4">{cfg.goalTitle}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {cfg.goals.map((g) => (
                <button key={g.slug} type="button" aria-pressed={goal === g.value || (g.kind && kind === g.kind && !g.value)}
                  onClick={() => { if (g.kind) { setParam('kind', kind === g.kind ? '' : g.kind); setParam('goal', ''); } else { setParam('goal', goal === g.value ? '' : g.value); } }}
                  className={`text-left bg-white border-2 rounded-3xl p-4 min-h-[44px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-700 ${(g.kind ? kind === g.kind : goal === g.value) ? 'border-teal-500 bg-teal-50/50' : 'border-teal-100 hover:border-teal-400'}`}
                  data-testid={`${cfg.tid}-goal-${g.slug}`}>
                  <p className="font-bold text-gray-900 text-[15px]">{g.label}</p>
                  <p className="text-[13px] text-gray-600 mt-1">{g.sub}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Steps strip */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
          <h2 className="text-base md:text-lg font-bold text-gray-900 uppercase tracking-widest mb-4">{cfg.stepsTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {cfg.steps.map((s, i) => (
              <div key={i} className="bg-white border-2 border-teal-100 rounded-3xl px-4 py-4 flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-teal-600 text-white text-sm font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                <p className="text-sm text-gray-700 font-semibold leading-snug mt-1">{s}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[13px] text-gray-600 bg-white border border-teal-100 rounded-2xl px-4 py-3 leading-relaxed flex gap-2" data-testid={`${cfg.tid}-geo-notice`}>
            <Globe size={14} className="text-teal-600 shrink-0 mt-0.5" aria-hidden="true" /> {GEO_NOTICE}
          </p>
        </section>

        {/* Search + filters + grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
          <div className="relative max-w-xl">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input type="search" value={q} onChange={(e) => setParam('q', e.target.value)} placeholder={cfg.searchPh} aria-label={cfg.searchPh}
              className="w-full min-h-[48px] pl-11 pr-11 py-3 rounded-full bg-white border-2 border-teal-100 focus:border-teal-500 outline-none text-[15px] text-gray-900 placeholder:text-gray-400 transition-colors" data-testid={`${cfg.tid}-search-input`} />
            {q && (
              <button type="button" onClick={() => setParam('q', '')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center" data-testid={`${cfg.tid}-search-clear`}>
                <X size={14} />
              </button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filter resources">
            {cfg.filters.map((f) => (
              <button key={f.value} type="button" aria-pressed={kind === f.value} onClick={() => setParam('kind', f.value === 'all' ? '' : f.value)} className={pillCls(kind === f.value)} data-testid={`${cfg.tid}-filter-${f.value}`}>
                {f.label}
              </button>
            ))}
            {(q || kind !== 'all' || goal) && (
              <button type="button" onClick={() => setParams(new URLSearchParams())} className="min-h-[44px] px-4 py-2 text-[13px] font-bold text-teal-700 hover:text-teal-900 underline underline-offset-4" data-testid={`${cfg.tid}-clear-filters`}>
                Clear all filters
              </button>
            )}
          </div>
          <p className="mt-4 text-sm font-semibold text-gray-600" aria-live="polite" data-testid={`${cfg.tid}-results-count`}>
            Showing {visible.length} of {filtered.length} official destinations{filtered.length !== pool.length ? ` (from ${pool.length} total)` : ''}. Directory references — not a live vacancy feed.
          </p>

          {visible.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {visible.map((r) => (
                <article key={r.id} className="bg-white border-2 border-teal-100 hover:border-teal-300 rounded-3xl p-5 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg motion-reduce:transform-none" data-testid={`${cfg.tid}-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-gray-900 text-lg leading-snug">{r.name}</h3>
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                      <BadgeCheck size={10} aria-hidden="true" /> {KIND_LABEL[r.kind] || r.kind}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{cfg.fit === 'fresher_launchpad' ? r.preview_fresher : r.preview_professional}</p>
                  {(r.categories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {r.categories.slice(0, 3).map((c) => (
                        <span key={c} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-600">{c}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-[13px] text-gray-700 mt-3"><span className="font-bold text-teal-800">Eligibility: </span>{r.eligibility}</p>
                  <p className="text-[13px] text-gray-500 mt-1.5"><span className="font-bold text-gray-700">Availability: </span>{r.availability}</p>
                  <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => setDetail(r)}
                      className="min-h-[44px] px-4 py-2 rounded-full border-2 border-teal-600 text-teal-700 hover:bg-teal-50 font-bold text-[13px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                      data-testid={`${cfg.tid}-details-btn-${r.id}`}>
                      View details
                    </button>
                    <a href={r.official_url} target="_blank" rel="noopener noreferrer"
                      aria-label={`Visit the official ${r.name} site (opens in a new tab, leaving LearnixTa)`}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-[13px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-800"
                      data-testid={`${cfg.tid}-link-${r.id}`}>
                      {r.cta || 'Visit official destination'} <ExternalLink size={13} aria-hidden="true" />
                    </a>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">{domainOf(r.official_url)} · External official site · Opens in a new tab · Reviewed {r.content_reviewed_at || r.reviewed_at}</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-8 bg-white border-2 border-dashed border-teal-200 rounded-3xl px-6 py-12 text-center" data-testid={`${cfg.tid}-empty-state`}>
              <p className="font-bold text-gray-900">No resources match your search.</p>
              <button type="button" onClick={() => setParams(new URLSearchParams())} className="mt-4 min-h-[44px] px-5 py-2.5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm" data-testid={`${cfg.tid}-empty-clear`}>Clear all filters</button>
            </div>
          )}

          {filtered.length > shown && (
            <div className="mt-8 text-center">
              <button type="button" onClick={() => setShown((n) => n + PAGE)} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-7 py-3 rounded-full border-2 border-teal-600 text-teal-700 hover:bg-teal-50 font-bold text-sm transition-colors" data-testid={`${cfg.tid}-load-more`}>
                Load 12 more <ChevronDown size={15} aria-hidden="true" />
              </button>
            </div>
          )}
        </section>

        <PathwayGuides cfg={cfg} />

        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-12">
          <p className="text-sm text-gray-800 bg-amber-50 border-2 border-amber-200 rounded-2xl px-5 py-4 leading-relaxed" data-testid={`${cfg.tid}-safety-notice`}>
            <ShieldCheck size={15} className="inline -mt-0.5 mr-1.5 text-amber-700" aria-hidden="true" /> {data.safety_panel}
          </p>
          <p className="text-[13px] text-gray-500 leading-relaxed border-t border-gray-200 pt-6 mt-8" data-testid={`${cfg.tid}-disclaimer`}>{data.independence_disclaimer}</p>
        </section>
      </main>
      {detail && <ResourceModal r={detail} cfg={cfg} onClose={() => setDetail(null)} />}
      <PathwayDisclaimer />
      <Footer />
    </div>
  );
}

const FILTERS_FRESHER = [
  { value: 'all', label: 'All' },
  { value: 'government_or_public_careers', label: 'Government & public careers' },
  { value: 'apprenticeship_or_internship', label: 'Apprenticeships & internships' },
  { value: 'company_careers', label: 'Company careers' },
  { value: 'official_learning', label: 'Official learning' },
];
const FILTERS_SWITCH = [
  { value: 'all', label: 'All' },
  { value: 'company_careers', label: 'Company careers' },
  { value: 'official_learning', label: 'Official learning' },
  { value: 'government_or_public_careers', label: 'Government & public careers' },
  { value: 'return_to_work', label: 'Return to work' },
];

export function FresherLaunchpad() {
  return <ResourcePathway cfg={{
    tid: 'fl', fit: 'fresher_launchpad', title: 'Fresher Launchpad',
    eyebrow: data.pathways?.fresher_launchpad?.eyebrow || 'First Job · Official Routes',
    heading: data.pathways?.fresher_launchpad?.hero_heading || 'Start your career with official destinations.',
    copy: 'Explore Government services, early-career hubs, apprenticeships and official learning destinations — compare real work, entry routes, costs and AI changes before a major commitment.',
    trust: 'For Indian freshers · Official destinations · Directory references, not a live vacancy feed',
    stepsTitle: 'How this works',
    steps: ['Choose a direction', 'Build one useful skill', 'Prepare your application', 'Apply on the official site'],
    searchPh: 'Search by company, field, apprenticeship or skill',
    filters: FILTERS_FRESHER,
    goalTitle: 'Not sure where to start?',
    goals: [
      { slug: 'govt', label: 'Start with Government and public routes', sub: 'Public-sector career services.', kind: 'government_or_public_careers' },
      { slug: 'company', label: 'Explore company career hubs', sub: 'Official early-career pages of major employers.', kind: 'company_careers' },
      { slug: 'learning', label: 'Build skills on official learning platforms', sub: 'Free and official upskilling destinations.', kind: 'official_learning' },
      { slug: 'appr', label: 'Find apprenticeships & internships', sub: 'National apprenticeship and internship portals.', kind: 'apprenticeship_or_internship' },
    ],
  }} />;
}

export function CareerSwitchGrowth() {
  return <ResourcePathway cfg={{
    tid: 'cs', fit: 'career_switch_growth', title: 'Career Switch & Growth',
    eyebrow: data.pathways?.career_switch_growth?.eyebrow || 'Next Move · Official Routes',
    heading: data.pathways?.career_switch_growth?.hero_heading || 'Plan your next move with evidence and financial clarity.',
    copy: 'Explore 207 experienced-career hubs, official learning paths and return-to-work routes — compare real work, entry routes, costs and AI changes before resigning or paying for training.',
    trust: 'For working professionals in India · Official destinations · Directory references, not a live vacancy feed',
    stepsTitle: 'How to use this pathway',
    steps: ['Pick a goal', 'Compare requirements', 'Close a skill gap', 'Continue on the exact official domain'],
    searchPh: 'Search by target role, skill, company or return-to-work route',
    filters: FILTERS_SWITCH,
    goalTitle: 'What is your goal?',
    goals: [
      { slug: 'switch', label: 'Switch fields', sub: 'Move into a different domain or industry.', value: 'switch' },
      { slug: 'upskill', label: 'Upskill for a role', sub: 'Close a specific skill gap with official learning.', value: 'upskill' },
      { slug: 'return', label: 'Return after a break', sub: 'Return-to-work and returnship routes.', kind: 'return_to_work' },
      { slug: 'grow', label: 'Grow in my current field', sub: 'Advance where you already work.', value: 'grow' },
    ],
  }} />;
}
